import React, { useState, useEffect } from "react";
import {
    Layout, Row, Col, Card, Breadcrumb, message, Input, Button, Tag, Empty, Spin, Tooltip, Badge
} from "antd";
import {
    HomeOutlined, GlobalOutlined, AppstoreOutlined, BuildOutlined,
    ExperimentOutlined, ArrowLeftOutlined, SearchOutlined, EnvironmentOutlined,
    InfoCircleOutlined, RightOutlined
} from "@ant-design/icons";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import api from "../../../services/axiosInstance";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const SystemVisualizer = () => {
    const [level, setLevel] = useState("lands"); // lands -> sections -> fields -> plants
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 24 });
    const [search, setSearch] = useState("");
    const [selection, setSelection] = useState({
        land: null,
        section: null,
        field: null
    });

    const fetchData = async (currentLevel, filters = {}, append = false) => {
        setLoading(true);
        try {
            let endpoint = "";
            let params = { 
                limit: currentLevel === "plants" ? pagination.pageSize : 100,
                page: currentLevel === "plants" ? filters.page || 1 : 1
            };

            if (currentLevel === "lands") {
                endpoint = "/farmSetUp/land/list.php";
            } else if (currentLevel === "sections") {
                endpoint = "/farmSetUp/section/list.php";
                params.land_id = filters.landId;
            } else if (currentLevel === "fields") {
                endpoint = "/farmSetUp/fields/list.php";
                params.land_id = filters.landId;
                params.section_id = filters.sectionId;
            } else if (currentLevel === "plants") {
                endpoint = "/farmSetUp/plant/list.php";
                params.land_id = filters.landId;
                params.section_id = filters.sectionId;
                params.field_id = filters.fieldId;
                if (search) params.search = search;
            }

            const res = await api.get(endpoint, { params });
            const newData = res.data.dataset || [];
            
            if (append) {
                setData(prev => [...prev, ...newData]);
            } else {
                setData(newData);
            }

            if (res.data.pagination) {
                setPagination({
                    current: res.data.pagination.current_page,
                    total: res.data.pagination.total_records,
                    pageSize: res.data.pagination.per_page
                });
            }
        } catch (error) {
            console.error(error);
            message.error(`Failed to load ${currentLevel}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setData([]); // Clear data when level or hierarchy changes
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData(level, {
            landId: selection.land?.land_id,
            sectionId: selection.section?.section_id,
            fieldId: selection.field?.field_id,
            page: 1
        });
    }, [level, selection.land, selection.section, selection.field]);

    const handleSearch = () => {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData(level, {
            landId: selection.land?.land_id,
            sectionId: selection.section?.section_id,
            fieldId: selection.field?.field_id,
            page: 1
        });
    };

    const loadMore = () => {
        const nextPage = pagination.current + 1;
        fetchData(level, {
            landId: selection.land?.land_id,
            sectionId: selection.section?.section_id,
            fieldId: selection.field?.field_id,
            page: nextPage
        }, true);
    };

    const navigateBack = () => {
        if (level === "plants") {
            setLevel("fields");
            setSelection(prev => ({ ...prev, field: null }));
        } else if (level === "fields") {
            setLevel("sections");
            setSelection(prev => ({ ...prev, section: null }));
        } else if (level === "sections") {
            setLevel("lands");
            setSelection(prev => ({ ...prev, land: null }));
        }
    };

    const selectItem = (item) => {
        if (level === "lands") {
            setSelection(prev => ({ ...prev, land: item }));
            setLevel("sections");
        } else if (level === "sections") {
            setSelection(prev => ({ ...prev, section: item }));
            setLevel("fields");
        } else if (level === "fields") {
            setSelection(prev => ({ ...prev, field: item }));
            setLevel("plants");
        }
    };

    const getLevelConfig = () => {
        switch (level) {
            case "lands":
                return {
                    title: "AgroLand",
                    icon: <GlobalOutlined />,
                    image: "/assets/images/visualizer/land.jpg",
                    nameKey: "land_name",
                    codeKey: "land_id", // Just a fallback
                    descKey: "location"
                };
            case "sections":
                return {
                    title: `Sections in ${selection.land?.land_name}`,
                    icon: <AppstoreOutlined />,
                    image: "/assets/images/visualizer/section.png",
                    nameKey: "section_name",
                    codeKey: "section_code",
                    descKey: "section_id"
                };
            case "fields":
                return {
                    title: `Fields in ${selection.section?.section_name}`,
                    icon: <BuildOutlined />,
                    image: "/assets/images/visualizer/field.png",
                    nameKey: "field_code",
                    codeKey: "field_code",
                    descKey: "field_id"
                };
            case "plants":
                return {
                    title: `Plants in Field ${selection.field?.field_code}`,
                    icon: <ExperimentOutlined />,
                    image: "/assets/images/visualizer/plant.png",
                    nameKey: "plant_code",
                    codeKey: "plant_code",
                    descKey: "pc_name"
                };
            default:
                return {};
        }
    };

    const config = getLevelConfig();

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: <span style={{ cursor: 'pointer' }}>Farm Overview</span>, onClick: () => { setLevel("lands"); setSelection({ land: null, section: null, field: null }); } },
                                ...(selection.land ? [{ title: <span style={{ cursor: 'pointer' }}>{selection.land.land_name}</span>, onClick: () => { setLevel("sections"); setSelection(prev => ({ ...prev, section: null, field: null })); } }] : []),
                                ...(selection.section ? [{ title: <span style={{ cursor: 'pointer' }}>{selection.section.section_name}</span>, onClick: () => { setLevel("fields"); setSelection(prev => ({ ...prev, field: null })); } }] : []),
                                ...(selection.field ? [{ title: selection.field.field_code }] : []),
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            {config.icon} {config.title}
                        </h1>
                    </Col>
                    <Col xs={24} md={8}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                            {level !== "lands" && (
                                <Button 
                                    icon={<ArrowLeftOutlined />} 
                                    onClick={navigateBack}
                                    className="fluid-btn-secondary"
                                >
                                    Back
                                </Button>
                            )}
                            <Input 
                                placeholder={`Search by code...`} 
                                prefix={<SearchOutlined />} 
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onPressEnter={handleSearch}
                                className="fluid-input"
                                style={{ borderRadius: '12px' }}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                {/* Stats Summary */}
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={8}>
                        <div className="visualizer-mini-stat">
                            <div className="stat-label">Total {level.charAt(0).toUpperCase() + level.slice(1)}</div>
                            <div className="stat-value">{pagination.total || data.length}</div>
                        </div>
                    </Col>
                    {selection.land && (
                        <Col xs={24} sm={8}>
                            <div className="visualizer-mini-stat">
                                <div className="stat-label">Selected Land</div>
                                <div className="stat-value truncate-text" style={{ fontSize: '1rem', width: '100%' }}>{selection.land.land_name}</div>
                            </div>
                        </Col>
                    )}
                    {selection.section && (
                        <Col xs={24} sm={8}>
                            <div className="visualizer-mini-stat">
                                <div className="stat-label">Selected Section</div>
                                <div className="stat-value truncate-text" style={{ fontSize: '1rem', width: '100%' }}>{selection.section.section_name}</div>
                            </div>
                        </Col>
                    )}
                </Row>

                <Spin spinning={loading && data.length === 0}>
                    {data.length === 0 && !loading ? (
                        <div style={{ padding: '100px 0' }}>
                            <Empty description={`No ${level} found.`} />
                        </div>
                    ) : (
                        <>
                            <Row gutter={[20, 20]}>
                                {data.map((item, idx) => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                                        <Card
                                            hoverable
                                            className="fluid-card visualizer-card"
                                            cover={
                                                <div className="visualizer-card-cover">
                                                    <img alt="cover" src={config.image} />
                                                    <div className="visualizer-card-overlay">
                                                        <Tag color="green" className="visualizer-tag">
                                                            {item.status || "Active"}
                                                        </Tag>
                                                    </div>
                                                </div>
                                            }
                                            onClick={() => level !== "plants" && selectItem(item)}
                                        >
                                            <Card.Meta
                                                title={
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--fluid-primary)' }}>
                                                            {item[config.nameKey]}
                                                        </span>
                                                        {level !== "plants" && <RightOutlined style={{ fontSize: '12px', color: '#ccc' }} />}
                                                    </div>
                                                }
                                                description={
                                                    <div className="visualizer-card-desc">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px', opacity: 0.8 }}>
                                                            <InfoCircleOutlined />
                                                            <span className="truncate-text">{item[config.descKey] || 'No description'}</span>
                                                        </div>
                                                        {level === "plants" && (
                                                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Badge status="processing" text={`${item.pc_name || "Plant"}`} />
                                                                <span style={{ fontSize: '10px', opacity: 0.6 }}>{item.planted_date || "N/A"}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                }
                                            />
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* Load More Button for Plants or large datasets */}
                            {data.length < pagination.total && (
                                <div style={{ textAlign: 'center', marginTop: '40px', paddingBottom: '20px' }}>
                                    <Button 
                                        type="primary" 
                                        size="large" 
                                        onClick={loadMore} 
                                        loading={loading}
                                        className="fluid-btn-primary"
                                        style={{ minWidth: '200px' }}
                                    >
                                        Load More {level.charAt(0).toUpperCase() + level.slice(1)} ({data.length} of {pagination.total})
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Spin>
            </Content>

            <style>{`
                .visualizer-mini-stat {
                    background: rgba(255, 255, 255, 0.6);
                    backdrop-filter: blur(8px);
                    padding: 16px;
                    border-radius: 16px;
                    border: 1px solid rgba(255, 255, 255, 0.4);
                    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                    text-align: center;
                }
                .visualizer-mini-stat .stat-label {
                    font-size: 11px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    color: var(--fluid-text-muted);
                    margin-bottom: 4px;
                    font-weight: 600;
                }
                .visualizer-mini-stat .stat-value {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: var(--fluid-primary-dark);
                }
                .visualizer-card {
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    border: 1px solid rgba(255,255,255,0.4);
                    background: rgba(255,255,255,0.7);
                    backdrop-filter: blur(10px);
                    border-radius: 20px;
                }
                .visualizer-card:hover {
                    transform: translateY(-12px) scale(1.02);
                    box-shadow: 0 20px 40px rgba(46, 125, 50, 0.15);
                    border-color: var(--fluid-primary-light);
                }
                .visualizer-card-cover {
                    height: 160px;
                    position: relative;
                    overflow: hidden;
                }
                .visualizer-card-cover img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.6s ease;
                }
                .visualizer-card:hover .visualizer-card-cover img {
                    transform: scale(1.15);
                }
                .visualizer-card-overlay {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    z-index: 2;
                }
                .visualizer-tag {
                    border-radius: 20px;
                    padding: 4px 14px;
                    font-weight: 700;
                    backdrop-filter: blur(6px);
                    background: rgba(255,255,255,0.95) !important;
                    color: var(--fluid-primary) !important;
                    border: 1px solid rgba(46, 125, 50, 0.1);
                    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
                }
                .visualizer-card-desc {
                    color: var(--fluid-text-muted);
                }
                .truncate-text {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                    display: inline-block;
                }
                .ant-card-meta-title {
                    margin-bottom: 0 !important;
                }
            `}</style>

            <Footerdiv />
        </Layout>
    );
};

export default SystemVisualizer;
